import React, { useState } from 'react';
import { Upload, Download, Database, AlertCircle, CheckCircle2, Loader2, FileJson, ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Data Migration Component
 * Allows admins to import/export data between preview and production environments
 */
const DataMigration = () => {
  const { token } = useAuth();
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [results, setResults] = useState([]);
  const [error, setError] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [importData, setImportData] = useState(null);

  // Collections that can be migrated
  const collections = [
    { name: 'services', label: 'Services', priority: 1 },
    { name: 'projects', label: 'Projects', priority: 1 },
    { name: 'sectors', label: 'Sectors', priority: 1 },
    { name: 'team', label: 'Team Members', priority: 1 },
    { name: 'news', label: 'News Articles', priority: 1 },
    { name: 'accreditations', label: 'Accreditations', priority: 2 },
    { name: 'testimonials', label: 'Testimonials', priority: 2 },
    { name: 'pages', label: 'Pages', priority: 2 },
    { name: 'job_listings', label: 'Job Listings', priority: 2 },
    { name: 'career_levels', label: 'Career Levels', priority: 2 },
    { name: 'site_settings', label: 'Site Settings', priority: 3 },
    { name: 'company_info', label: 'Company Info', priority: 3 },
    { name: 'contact_settings', label: 'Contact Settings', priority: 3 },
    { name: 'services_overview_settings', label: 'Services Overview Settings', priority: 3 },
    { name: 'news_settings', label: 'News Settings', priority: 3 },
    { name: 'images', label: 'Image Library', priority: 3 },
  ];

  // Export all collections to a JSON file
  const handleExportAll = async () => {
    setExporting(true);
    setError('');
    const exportData = {
      exported_at: new Date().toISOString(),
      collections: {}
    };

    try {
      for (const coll of collections) {
        const response = await fetch(`${API_URL}/api/admin/data-export/${coll.name}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          exportData.collections[coll.name] = data.documents;
        }
      }

      // Download as JSON file
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `furd-data-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setResults([{ type: 'success', message: 'Data exported successfully!' }]);
    } catch (err) {
      setError('Failed to export data: ' + err.message);
    } finally {
      setExporting(false);
    }
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const data = JSON.parse(event.target.result);
          setImportData(data);
          setError('');
        } catch (err) {
          setError('Invalid JSON file');
          setImportData(null);
        }
      };
      reader.readAsText(file);
    }
  };

  // Import all collections from the loaded file
  const handleImportAll = async (replaceExisting = false) => {
    if (!importData || !importData.collections) {
      setError('No valid data to import');
      return;
    }

    setImporting(true);
    setError('');
    setResults([]);
    const newResults = [];

    try {
      for (const [collName, documents] of Object.entries(importData.collections)) {
        if (!documents || documents.length === 0) continue;

        try {
          const response = await fetch(`${API_URL}/api/admin/data-import`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              collection: collName,
              documents: documents,
              replace_existing: replaceExisting
            })
          });

          if (response.ok) {
            const result = await response.json();
            newResults.push({
              type: 'success',
              message: `${collName}: Imported ${result.inserted_count} documents`
            });
          } else {
            const errorData = await response.json();
            newResults.push({
              type: 'warning',
              message: `${collName}: ${errorData.detail || 'Import failed'}`
            });
          }
        } catch (err) {
          newResults.push({
            type: 'error',
            message: `${collName}: ${err.message}`
          });
        }
      }

      setResults(newResults);
    } catch (err) {
      setError('Import failed: ' + err.message);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-gray-200 pb-4">
        <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
          <Database className="w-5 h-5 text-blue-600" />
          Data Migration
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          Export data from this environment or import data from another environment.
        </p>
      </div>

      {/* Warning Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-amber-800">Important</h3>
            <p className="text-sm text-amber-700 mt-1">
              This tool is designed for migrating content between Preview and Production environments.
              Always export and backup your data before performing imports with "Replace Existing" enabled.
            </p>
          </div>
        </div>
      </div>

      {/* Export Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="font-medium text-gray-900 flex items-center gap-2 mb-4">
          <Download className="w-4 h-4 text-green-600" />
          Export Data
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Download all CMS content as a JSON file. This can be imported into another environment.
        </p>
        <button
          onClick={handleExportAll}
          disabled={exporting}
          className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {exporting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Exporting...
            </>
          ) : (
            <>
              <Download className="w-4 h-4" />
              Export All Data
            </>
          )}
        </button>
      </div>

      {/* Import Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="font-medium text-gray-900 flex items-center gap-2 mb-4">
          <Upload className="w-4 h-4 text-blue-600" />
          Import Data
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Upload a previously exported JSON file to import data into this environment.
        </p>

        {/* File Upload */}
        <div className="mb-4">
          <label className="block">
            <span className="sr-only">Choose file</span>
            <input
              type="file"
              accept=".json"
              onChange={handleFileSelect}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
            />
          </label>
        </div>

        {/* File Info */}
        {importData && (
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <FileJson className="w-4 h-4 text-blue-600" />
              <span className="font-medium text-gray-900">{selectedFile?.name}</span>
            </div>
            <p className="text-sm text-gray-600">
              Exported: {new Date(importData.exported_at).toLocaleString()}
            </p>
            <p className="text-sm text-gray-600">
              Collections: {Object.keys(importData.collections || {}).length}
            </p>
            <div className="mt-2 flex flex-wrap gap-1">
              {Object.entries(importData.collections || {}).map(([name, docs]) => (
                <span
                  key={name}
                  className="inline-flex items-center px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded"
                >
                  {name}: {docs?.length || 0}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Import Buttons */}
        <div className="flex gap-3">
          <button
            onClick={() => handleImportAll(false)}
            disabled={importing || !importData}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {importing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Importing...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Add to Existing
              </>
            )}
          </button>
          <button
            onClick={() => {
              if (window.confirm('This will DELETE all existing data in these collections and replace it with the imported data. Are you sure?')) {
                handleImportAll(true);
              }
            }}
            disabled={importing || !importData}
            className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ArrowRight className="w-4 h-4" />
            Replace Existing
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Results Display */}
      {results.length > 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-3">Import Results</h4>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {results.map((result, index) => (
              <div
                key={index}
                className={`flex items-center gap-2 text-sm ${
                  result.type === 'success' ? 'text-green-700' :
                  result.type === 'warning' ? 'text-amber-700' : 'text-red-700'
                }`}
              >
                {result.type === 'success' ? (
                  <CheckCircle2 className="w-4 h-4" />
                ) : (
                  <AlertCircle className="w-4 h-4" />
                )}
                {result.message}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 mb-2">How to Migrate Data</h4>
        <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
          <li>Log in to your <strong>Preview</strong> CMS and click "Export All Data"</li>
          <li>Save the downloaded JSON file</li>
          <li>Log in to your <strong>Production</strong> CMS</li>
          <li>Upload the JSON file and click "Replace Existing" to fully sync</li>
        </ol>
      </div>
    </div>
  );
};

export default DataMigration;
